import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { money } from "@/lib/format";
import { CalendarRange, Loader2, Save, Zap, TrendingUp, TrendingDown } from "lucide-react";

const EvmCard = ({ label, value, hint, good }) => (
  <div className="rounded-xl border border-border bg-card p-4" data-testid={`evm-${label.toLowerCase().replace(/\s/g,'-')}`}>
    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">{label}</div>
    <div className={`text-xl font-mono font-bold tabular-nums ${good === undefined ? "" : good ? "text-emerald-500" : "text-destructive"}`}>{value}</div>
    {hint && <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>}
  </div>
);

export default function ScheduleTab({ projectId }) {
  const qc = useQueryClient();
  const [acts, setActs] = useState(null);
  const [dirty, setDirty] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["schedule", projectId],
    queryFn: async () => (await api.get(`/projects/${projectId}/schedule`)).data,
  });
  useEffect(() => { if (data?.exists && !dirty) setActs(data.activities); }, [data]); // eslint-disable-line

  const generate = async () => {
    try {
      const { data: d } = await api.post(`/projects/${projectId}/schedule/generate`);
      setActs(d.activities); setDirty(false);
      qc.setQueryData(["schedule", projectId], d);
      toast.success("Schedule generated from BOQ");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const save = async () => {
    try {
      const { data: d } = await api.put(`/schedule/${data.id}`, { activities: acts });
      qc.setQueryData(["schedule", projectId], d); setDirty(false);
      toast.success("Schedule saved");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const upd = (i, f, v) => { setActs((a) => a.map((x, j) => j === i ? { ...x, [f]: v } : x)); setDirty(true); };

  if (isLoading) return <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  if (!data?.exists) {
    return (
      <div className="rounded-xl border border-dashed border-border py-16 text-center">
        <CalendarRange className="h-9 w-9 mx-auto text-muted-foreground mb-3" />
        <p className="font-semibold">No schedule yet</p>
        <p className="text-sm text-muted-foreground mb-4">Auto-build a Gantt timeline with critical path & EVM from your BOQ.</p>
        <button data-testid="generate-schedule-btn" onClick={generate} className="rounded-lg bg-primary text-primary-foreground px-5 py-2.5 font-bold hover:bg-orange-600 transition-colors inline-flex items-center gap-2"><Zap className="h-4 w-4" />Generate Schedule</button>
      </div>
    );
  }

  const evm = data.evm || {};
  const totalDays = data.project_duration_days || 1;
  const list = acts || data.activities;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <EvmCard label="SPI" value={evm.SPI} hint="Schedule perf." good={evm.SPI >= 1} />
        <EvmCard label="CPI" value={evm.CPI} hint="Cost perf." good={evm.CPI >= 1} />
        <EvmCard label="EAC" value={money(evm.EAC, 0)} hint={`BAC ${money(evm.BAC, 0)}`} />
        <EvmCard label="Duration" value={`${totalDays}d`} hint={`${evm.elapsed_days || 0}d elapsed`} />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <EvmCard label="Planned Value" value={money(evm.PV, 0)} />
        <EvmCard label="Earned Value" value={money(evm.EV, 0)} />
        <EvmCard label="Actual Cost" value={money(evm.AC, 0)} />
      </div>

      {/* Gantt */}
      <div className="rounded-xl border border-border bg-card p-5" data-testid="gantt-chart">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold flex items-center gap-2"><CalendarRange className="h-4 w-4 text-primary" /> Gantt Timeline</h3>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-4 rounded bg-primary" />Critical</span>
            <span className="flex items-center gap-1.5"><span className="h-2.5 w-4 rounded bg-sky-500" />Float</span>
          </div>
        </div>
        <div className="space-y-2.5">
          {list.map((a) => {
            const left = (a.start_day / totalDays) * 100;
            const width = Math.max((a.duration_days / totalDays) * 100, 2);
            return (
              <div key={a.id} className="flex items-center gap-3" data-testid={`gantt-row-${a.code}`}>
                <div className="w-40 shrink-0 text-sm truncate">
                  <span className="font-mono text-xs text-muted-foreground mr-1">{a.code}</span>{a.name}
                </div>
                <div className="flex-1 relative h-7 bg-secondary/40 rounded">
                  <div className={`absolute h-7 rounded flex items-center px-2 ${a.critical ? "bg-primary" : "bg-sky-500"}`}
                    style={{ left: `${left}%`, width: `${width}%` }}>
                    <span className="text-[10px] font-mono text-white/90 truncate">{a.duration_days}d</span>
                  </div>
                  {a.progress > 0 && (
                    <div className="absolute h-1.5 bg-white/70 rounded-full bottom-0.5"
                      style={{ left: `${left}%`, width: `${width * (a.progress / 100)}%` }} />
                  )}
                </div>
                <div className="w-24 shrink-0 text-right font-mono text-xs tabular-nums">{money(a.cost, 0)}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Progress table */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h3 className="font-semibold">Progress & Actuals</h3>
          <button data-testid="save-schedule-btn" onClick={save} disabled={!dirty}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-sm font-semibold hover:bg-secondary transition-colors disabled:opacity-50">
            <Save className="h-3.5 w-3.5" /> {dirty ? "Save" : "Saved"}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead><tr className="text-left text-[10px] font-mono uppercase tracking-wider text-muted-foreground border-b border-border/60">
              <th className="px-4 py-2">Activity</th><th className="px-2 py-2 text-right">Budget</th>
              <th className="px-2 py-2 text-right">Planned %</th><th className="px-2 py-2 text-right">Progress %</th>
              <th className="px-2 py-2 text-right">Actual Cost</th><th className="px-2 py-2 text-center">Status</th>
            </tr></thead>
            <tbody>
              {list.map((a, i) => (
                <tr key={a.id} className="border-b border-border/40" data-testid={`sched-act-${i}`}>
                  <td className="px-4 py-2"><span className="font-mono text-xs text-muted-foreground mr-1">{a.code}</span>{a.name}</td>
                  <td className="px-2 py-2 text-right font-mono tabular-nums">{money(a.cost, 0)}</td>
                  <td className="px-2 py-2 text-right font-mono tabular-nums text-muted-foreground">{a.planned_pct}%</td>
                  <td className="px-2 py-1 w-24"><input data-testid={`progress-${i}`} type="number" min="0" max="100" value={a.progress ?? 0}
                    onChange={(e) => upd(i, "progress", Number(e.target.value))}
                    className="w-20 rounded border border-input bg-popover px-2 py-1 text-right font-mono tabular-nums outline-none focus:border-primary" /></td>
                  <td className="px-2 py-1 w-28"><input data-testid={`actual-${i}`} type="number" value={a.actual_cost ?? ""} placeholder={String(Math.round(a.earned_value || 0))}
                    onChange={(e) => upd(i, "actual_cost", e.target.value === "" ? null : Number(e.target.value))}
                    className="w-24 rounded border border-input bg-popover px-2 py-1 text-right font-mono tabular-nums outline-none focus:border-primary" /></td>
                  <td className="px-2 py-2 text-center">
                    {a.critical ? <span className="text-[10px] font-mono uppercase text-primary">Critical</span> : <span className="text-[10px] font-mono uppercase text-sky-400">{a.float}d float</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
